import { Button, Flex, Text, useToast } from '@chakra-ui/react';
import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ClientUpdateNotificationContext } from './ClientUpdateNotificationContext';

interface UpdateEvent {
  newVersionAvailable: boolean;
  timestamp: number;
  message: string;
}

export const ClientUpdateNotification: FC<PropsWithChildren> = ({
  children,
}) => {
  const toast = useToast();
  const [isPuppeteer, setIsPuppeteer] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (window.ENVS.environment === 'e2e') {
      return;
    }

    try {
      eventSourceRef.current = new EventSource(
        '/client-message/sse/client-update',
        {
          withCredentials: false,
        }
      );

      eventSourceRef.current.onopen = () => {
        // eslint-disable-next-line no-console
        console.log('SSE connection established');
      };

      eventSourceRef.current.onmessage = (event) => {
        const data: UpdateEvent = JSON.parse(
          event.data as string
        ) as UpdateEvent;
        if (data.newVersionAvailable) {
          toast({
            title: 'New version available',
            description: (
              <Flex alignItems="center" gap={2} justifyContent="space-between">
                <Text>{data.message || 'Please refresh your browser'}</Text>
                <Button
                  bg="indigo.900"
                  color="white"
                  size="xs"
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </Button>
              </Flex>
            ),

            status: 'info',
            duration: null,
            isClosable: true,
          });
        }
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create EventSource:', error);
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [toast]);

  // Disables the client update notification if user source is puppeteer.
  // This is needed because puppeteer waits for networkidle0
  useEffect(() => {
    if (isPuppeteer) {
      eventSourceRef.current?.close();
    }
  }, [isPuppeteer]);

  return (
    <ClientUpdateNotificationContext.Provider
      value={{ isPuppeteer, setIsPuppeteer }}
    >
      {children}
    </ClientUpdateNotificationContext.Provider>
  );
};
